import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Hourglass, Inbox } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { LeadCard } from "@/components/partners/LeadCard";
import { PurchasedLead } from "@/components/partners/PurchasedLead";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";

const TX_STATUS = { initiated: "En attente", paid: "Payé", completed: "Payé", refunded: "Remboursé", expired: "Expiré", failed: "Échoué" };
const STATUS_TEXT = { pending: "Votre candidature est en cours de validation par l'équipe CASHMY. Vous serez notifié dès l'activation.",
  rejected: "Votre candidature n'a pas été retenue.", suspended: "Votre compte partenaire est suspendu. Contactez CASHMY." };

const Empty = ({ text }) => <div className="card-surface flex items-center gap-3 p-6 text-sm text-muted-foreground" data-testid="partner-empty"><Inbox className="h-5 w-5" />{text}</div>;

const Payments = () => {
  const { money, date } = useFormat();
  const { data } = useQuery({ queryKey: ["partner-payments"], queryFn: () => api.get("/partners/payments").then((r) => r.data) });
  if (!data) return <Skeleton className="h-32 rounded-2xl" />;
  if (!data.length) return <Empty text="Aucun paiement pour l'instant." />;
  return (
    <ul className="card-surface divide-y divide-border" data-testid="partner-payments">
      {data.map((t, i) => <li key={`${t.created_at}-${i}`} className="flex items-center justify-between gap-3 p-4 text-sm"><span>{date(t.created_at)} · Lead {t.mode === "exclusive" ? "exclusif" : "partagé"}</span>
        <span className="flex items-center gap-3"><span className="text-xs text-muted-foreground">{TX_STATUS[t.status]}</span><b className="num">{money(t.amount)}</b></span></li>)}
    </ul>
  );
};

export default function Partners() {
  const { user } = useAuth();
  const { section } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["partner-me"], queryFn: () => api.get("/partners/me").then((r) => r.data), enabled: !!user.partner });
  const approved = me.data?.status === "approved";
  const leads = useQuery({ queryKey: ["partner-leads"], queryFn: () => api.get("/partners/leads").then((r) => r.data), enabled: approved });
  useEffect(() => {
    if (params.get("paiement") === "annule") { toast.info("Paiement annulé : le lead n'a pas été acheté"); setParams({}, { replace: true }); }
  }, [params, setParams]);
  if (!user.partner) return <Navigate to="/" replace />;
  if (!me.data) return <Skeleton className="h-64 rounded-2xl" />;
  const p = me.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["partner-leads"] });
  const title = section === "achetes" ? "Mes leads" : section === "paiements" ? "Paiements" : "Leads disponibles";
  return (
    <div className="space-y-6" data-testid="partners-page">
      <PageHeader eyebrow={`Partenaire · ${p.company}`} title={title} text={`Secteurs : ${p.sector_labels.join(", ")}. Lead partagé ${p.prices.shared} € HT (jusqu'à 3 pros), lead exclusif ${p.prices.exclusive} € HT.`} />
      {!approved ? <div className="card-surface flex items-start gap-3 p-6" data-testid="partner-status-banner"><Hourglass className="mt-0.5 h-5 w-5 text-gold" /><p className="text-sm">{STATUS_TEXT[p.status]}</p></div>
        : section === "paiements" ? <Payments />
        : !leads.data ? <Skeleton className="h-64 rounded-2xl" />
        : section === "achetes" ? (leads.data.purchased.length ? <ul className="stagger grid gap-4 lg:grid-cols-2" data-testid="partner-bought-list">{leads.data.purchased.map((l) => <PurchasedLead key={l.id} lead={l} onChange={refresh} />)}</ul> : <Empty text="Vous n'avez encore acheté aucun lead." />)
        : leads.data.available.length ? <ul className="stagger grid gap-4 lg:grid-cols-2" data-testid="partner-leads-list">{leads.data.available.map((l) => <LeadCard key={l.id} lead={l} prices={p.prices} />)}</ul>
        : <Empty text="Aucune demande disponible dans vos secteurs pour le moment." />}
    </div>
  );
}
